/**
 * City Agency Pages — Wave 14
 * TRIPLE-HUB PRIORITY: Madrid (EU Hub), Dubai (MENA Hub) — branded as Call IT Dev offices
 * + Changsha, Xiamen (China), Kolkata (India), Fez (Morocco), Hamburg (Germany),
 * Barcelona (Spain), Doha-West-Bay (Qatar already exists → Kuwait City), Manchester (UK)
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, dashboards using React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with PCI-DSS and SOC 2.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant apps.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE14: Record<string, CityAgencyData> = {

  // =============================================
  // TRIPLE-HUB: MADRID — European Hub Office
  // =============================================
  madridHub: {
    slug: 'software-development-agency-madrid-hub',
    cityName: 'Madrid',
    countryName: 'Spain',
    flag: '🇪🇸',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Madrid — European Hub | Call IT Dev',
      fr: 'Agence Développement Logiciel Madrid — Hub Européen | Call IT Dev',
      es: 'Agencia Desarrollo Software Madrid — Hub Europeo | Call IT Dev',
      de: 'Softwareentwicklung Agentur Madrid — Europäisches Hub | Call IT Dev',
      pt: 'Agência Desenvolvimento Software Madrid — Hub Europeu | Call IT Dev',
      it: 'Agenzia Sviluppo Software Madrid — Hub Europeo | Call IT Dev',
      ar: 'وكالة تطوير برمجيات مدريد — المركز الأوروبي | Call IT Dev',
    },
    description: {
      en: 'Call IT Dev European Hub in Madrid, Spain. 200+ engineers, EU-based project management, GDPR-native. From €25/hr. Direct coordination with Casablanca HQ & Dubai office.',
      fr: 'Hub européen Call IT Dev à Madrid. 200+ ingénieurs, gestion de projet basée UE, RGPD natif. Dès 25 €/h. Coordination directe avec Casablanca et Dubaï.',
      es: 'Hub europeo de Call IT Dev en Madrid. 200+ ingenieros, gestión de proyectos UE, RGPD nativo. Desde 25 €/h. Coordinación directa con Casablanca y Dubái.',
      de: 'Europäisches Hub von Call IT Dev in Madrid. 200+ Ingenieure, EU-Projektmanagement, DSGVO-nativ. Ab 25 €/h.',
      pt: 'Hub europeu da Call IT Dev em Madrid. 200+ engenheiros, gestão EU, RGPD nativo. A partir de 25 €/h.',
      it: 'Hub europeo di Call IT Dev a Madrid. 200+ ingegneri, gestione UE, GDPR nativo. Da 25 €/h.',
      ar: 'مركز Call IT Dev الأوروبي في مدريد. 200+ مهندس، إدارة مشاريع أوروبية، GDPR أصلي. من 25 €/ساعة.',
    },
    keywords: ['software development agency Madrid', 'agencia desarrollo software Madrid', 'Call IT Dev Madrid', 'nearshore development Spain', 'EU software agency Madrid', 'desarrollo web Madrid'],
    heroTitle: {
      en: 'Call IT Dev European Hub — Madrid, Spain',
      fr: 'Hub Européen Call IT Dev — Madrid, Espagne',
      es: 'Hub Europeo Call IT Dev — Madrid, España',
      de: 'Call IT Dev Europäisches Hub — Madrid, Spanien',
      pt: 'Hub Europeu Call IT Dev — Madrid, Espanha',
      it: 'Hub Europeo Call IT Dev — Madrid, Spagna',
      ar: 'مركز Call IT Dev الأوروبي — مدريد، إسبانيا',
    },
    heroSubtitle: {
      en: 'Our European headquarters. EU-based project management, GDPR-native compliance, direct access to 200+ engineers across Casablanca & Madrid. Triple-Hub advantage: Casablanca × Madrid × Dubai.',
      fr: 'Notre siège européen. Gestion de projet basée UE, conformité RGPD native, accès direct à 200+ ingénieurs Casablanca & Madrid. Avantage Triple-Hub : Casablanca × Madrid × Dubaï.',
      es: 'Nuestra sede europea. Gestión UE, RGPD nativo, acceso directo a 200+ ingenieros. Ventaja Triple-Hub: Casablanca × Madrid × Dubái.',
      de: 'Unser europäisches Hauptquartier. EU-Projektmanagement, DSGVO-nativ. Triple-Hub: Casablanca × Madrid × Dubai.',
      pt: 'Nosso escritório europeu. Gestão UE, RGPD nativo. Triple-Hub: Casablanca × Madrid × Dubai.',
      it: 'Il nostro hub europeo. Gestione UE, GDPR nativo. Triple-Hub: Casablanca × Madrid × Dubai.',
      ar: 'مقرنا الأوروبي. إدارة مشاريع أوروبية، GDPR أصلي. ميزة Triple-Hub: الدار البيضاء × مدريد × دبي.',
    },
    whyTitle: {
      en: 'Why Our Madrid Hub for Your Project',
      fr: 'Pourquoi Notre Hub Madrid pour Votre Projet',
      es: 'Por Qué Nuestro Hub Madrid para Tu Proyecto',
      de: 'Warum Unser Madrid Hub für Ihr Projekt',
      pt: 'Por Que Nosso Hub Madrid para Seu Projeto',
      it: 'Perché il Nostro Hub Madrid per il Tuo Progetto',
      ar: 'لماذا مركز مدريد لمشروعك',
    },
    whyReasons: [
      { icon: '🇪🇺', title: 'EU-Based Entity', description: 'Spanish company, EU contracts, GDPR-native. Data sovereignty guaranteed within the European Union.' },
      { icon: '🔗', title: 'Triple-Hub Model', description: 'Seamless coordination: Madrid (EU sales & PM) ↔ Casablanca (200+ engineers) ↔ Dubai (MENA clients).' },
      { icon: '⏰', title: 'CET Timezone', description: 'Madrid operates in CET — same timezone as Paris, Berlin, Rome. Real-time collaboration.' },
      { icon: '🗣️', title: 'Multilingual PM', description: 'Project managers fluent in Spanish, French, English, Arabic. Native communication in your language.' },
      { icon: '💰', title: '55% vs Madrid Agencies', description: '€25-42/hr vs €80-130/hr locally. EU quality at nearshore-optimized prices.' },
      { icon: '✈️', title: '1h to Casablanca', description: 'Madrid-Casablanca: 1-hour flight, daily connections. Seamless team integration.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Is Call IT Dev based in Madrid?', answer: 'Yes. Madrid is our European Hub. We have a registered EU entity for contracts, invoicing, and GDPR compliance. Our delivery center is in Casablanca with 200+ engineers.' },
      { question: 'What is the Triple-Hub advantage?', answer: 'Casablanca (engineering), Madrid (EU sales/PM), Dubai (MENA clients). You get EU-level governance, nearshore pricing, and 24/7 coverage across 3 timezones.' },
      { question: 'Can I sign an EU contract?', answer: 'Absolutely. Our Madrid entity offers standard EU service agreements, compliant with Spanish and EU law.' },
      { question: 'How does GDPR work?', answer: 'EU-based project management, data processing agreements via our Madrid entity. Morocco has EU adequacy decision for additional security.' },
      { question: 'Do you have engineers in Madrid?', answer: 'We have project managers and solution architects in Madrid. Engineering delivery is from Casablanca (1h flight away, same timezone).' },
      { question: 'What languages do you support?', answer: 'Spanish, French, English, Arabic, German, Portuguese, Italian — native-level communication.' },
    ],
    localRate: '€80-130',
    ourRate: '€25-42',
    savings: '55%+',
  },

  // =============================================
  // TRIPLE-HUB: DUBAI — MENA Hub Office
  // =============================================
  dubaiHub: {
    slug: 'software-development-agency-dubai-hub',
    cityName: 'Dubai',
    countryName: 'UAE',
    flag: '🇦🇪',
    timezone: 'GST (GMT+4)',
    currency: 'AED/USD',
    title: {
      en: 'Software Development Agency Dubai — MENA Hub | Call IT Dev',
      fr: 'Agence Développement Logiciel Dubaï — Hub MENA | Call IT Dev',
      ar: 'وكالة تطوير برمجيات دبي — مركز الشرق الأوسط | Call IT Dev',
      es: 'Agencia Desarrollo Software Dubái — Hub MENA | Call IT Dev',
      de: 'Softwareentwicklung Agentur Dubai — MENA Hub | Call IT Dev',
    },
    description: {
      en: 'Call IT Dev MENA Hub in Dubai, UAE. Arabic/English bilingual teams, Sharia-compliant fintech, smart city solutions. From $25/hr. Triple-Hub: Casablanca × Madrid × Dubai.',
      fr: 'Hub MENA Call IT Dev à Dubaï. Équipes bilingues arabe/anglais, fintech conforme à la charia. Dès 25 $/h. Triple-Hub : Casablanca × Madrid × Dubaï.',
      ar: 'مركز Call IT Dev للشرق الأوسط في دبي. فرق ثنائية اللغة عربي/إنجليزي، فنتك متوافق مع الشريعة. من 25$/ساعة.',
      es: 'Hub MENA de Call IT Dev en Dubái. Equipos bilingües árabe/inglés, fintech conforme a la sharia. Desde 25 $/h.',
      de: 'Call IT Dev MENA Hub in Dubai. Zweisprachige Teams AR/EN, Scharia-konforme Fintech. Ab 25 $/h.',
    },
    keywords: ['software development agency Dubai', 'IT outsourcing Dubai', 'Call IT Dev Dubai', 'nearshore development UAE', 'software agency Dubai MENA', 'تطوير برمجيات دبي'],
    heroTitle: {
      en: 'Call IT Dev MENA Hub — Dubai, UAE',
      fr: 'Hub MENA Call IT Dev — Dubaï, EAU',
      ar: 'مركز Call IT Dev للشرق الأوسط — دبي، الإمارات',
      es: 'Hub MENA Call IT Dev — Dubái, EAU',
      de: 'Call IT Dev MENA Hub — Dubai, VAE',
    },
    heroSubtitle: {
      en: 'Our MENA headquarters. Arabic/English bilingual teams, Sharia-compliant fintech expertise, Vision 2030 alignment. Triple-Hub: Casablanca × Madrid × Dubai.',
      fr: 'Notre siège MENA. Équipes bilingues arabe/anglais, expertise fintech charia. Triple-Hub : Casablanca × Madrid × Dubaï.',
      ar: 'مقرنا في الشرق الأوسط. فرق ثنائية اللغة، خبرة فنتك متوافقة مع الشريعة. Triple-Hub: الدار البيضاء × مدريد × دبي.',
      es: 'Nuestra sede MENA. Equipos bilingües, fintech sharia. Triple-Hub: Casablanca × Madrid × Dubái.',
      de: 'Unser MENA-Hauptquartier. Zweisprachige Teams, Scharia-Fintech. Triple-Hub: Casablanca × Madrid × Dubai.',
    },
    whyTitle: {
      en: 'Why Our Dubai Hub for Your Project',
      fr: 'Pourquoi Notre Hub Dubaï pour Votre Projet',
      ar: 'لماذا مركز دبي لمشروعك',
      es: 'Por Qué Nuestro Hub Dubái para Tu Proyecto',
      de: 'Warum Unser Dubai Hub für Ihr Projekt',
    },
    whyReasons: [
      { icon: '🌍', title: 'MENA Gateway', description: 'Dubai is the business gateway to the Middle East, North Africa, and South Asia. We serve the entire region.' },
      { icon: '🔗', title: 'Triple-Hub Model', description: 'Dubai (MENA sales & PM) ↔ Casablanca (200+ engineers) ↔ Madrid (EU compliance). Follow-the-sun coverage.' },
      { icon: '🗣️', title: 'Arabic/English Native', description: 'Native Arabic and English teams. Perfect for GCC, Levant, and North African markets.' },
      { icon: '🕌', title: 'Islamic Finance', description: 'Deep expertise in Sharia-compliant fintech, halal e-commerce, and Islamic banking systems.' },
      { icon: '💰', title: '60% vs Dubai Agencies', description: '$25-40/hr vs $80-150/hr locally. Engineering from Casablanca, PM from Dubai.' },
      { icon: '🏙️', title: 'Smart City Expertise', description: 'Dubai Smart City, NEOM, Vision 2030 — we build the tech that powers the future.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Is Call IT Dev based in Dubai?', answer: 'Yes. Dubai is our MENA Hub via our entity UBTIS. We handle GCC client relationships, Sharia-compliant projects, and regional coordination from Dubai.' },
      { question: 'What is the Triple-Hub advantage?', answer: 'Dubai (MENA sales), Madrid (EU compliance), Casablanca (engineering). You get regional expertise, competitive pricing, and multi-timezone coverage.' },
      { question: 'Can I sign a UAE contract?', answer: 'Yes. Our Dubai entity (UBTIS) provides UAE-compliant service agreements.' },
      { question: 'Do you work on Saudi Vision 2030 projects?', answer: 'Yes. Our Dubai team coordinates Saudi projects with Arabic-native engineers and Vision 2030 alignment.' },
      { question: 'What about data residency?', answer: 'We support UAE/Saudi data residency requirements and comply with regional cybersecurity frameworks.' },
      { question: 'Contact in Dubai?', answer: 'Call us at +971 5 58 17 05 08 or email contact@ubtis.com.' },
    ],
    localRate: '$80-150',
    ourRate: '$25-40',
    savings: '60%+',
  },

  // =============================================
  // CHINA CONTINUED
  // =============================================
  changsha: {
    slug: 'software-development-agency-changsha',
    cityName: 'Changsha',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY',
    title: {
      en: 'Software Development Agency Changsha | Call IT Dev',
      zh: '长沙软件开发公司 | Call IT Dev',
      fr: 'Agence Développement Logiciel Changsha | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Changsha, China. AI & media tech hub, home to top universities. From $15/hr.',
      zh: '长沙软件开发公司。AI和媒体科技中心，顶尖高校聚集地。15美元/小时起。',
      fr: 'Agence de développement logiciel à Changsha, Chine. Hub IA & médias, dès 15 $/h.',
    },
    keywords: ['software development Changsha', '长沙软件开发', 'IT outsourcing Changsha'],
    heroTitle: { en: 'Software Development Agency in Changsha', zh: '长沙软件开发公司', fr: 'Agence de Développement Logiciel à Changsha' },
    heroSubtitle: {
      en: "Changsha's booming tech scene. AI, media tech, and gaming. Home to NUDT and Hunan University. 40% below Tier 1 prices.",
      zh: '长沙蓬勃发展的科技生态。AI、媒体科技和游戏。国防科大和湖南大学所在地。低于一线城市40%。',
      fr: "Scène tech en plein essor à Changsha. IA, médias et jeux. 40% moins cher que les villes Tier 1.",
    },
    whyTitle: { en: 'Why Changsha for Software Development', zh: '为什么选择长沙进行软件开发', fr: 'Pourquoi Changsha' },
    whyReasons: [
      { icon: '🤖', title: 'AI Hub', description: 'NUDT (National University of Defense Technology) drives cutting-edge AI research.' },
      { icon: '📱', title: 'Media & Entertainment', description: 'Home to major Chinese media companies. Strong UX, gaming, and streaming tech.' },
      { icon: '💰', title: '40% Below Tier 1', description: 'Significantly cheaper than Beijing/Shanghai with rapidly growing talent pool.' },
      { icon: '🚄', title: 'HSR Connected', description: 'High-speed rail to Guangzhou (2h), Wuhan (1.5h), Shanghai (4.5h).' },
      { icon: '🎓', title: 'Top Universities', description: 'NUDT, Hunan University, Central South University — strong STEM output.' },
      { icon: '🏗️', title: 'Tech Parks', description: 'Changsha Hi-Tech Zone and Yuelu Mountain innovation corridor.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Why Changsha?', answer: 'AI research hub (NUDT), media tech capital, 40% below Tier 1 prices, and strong universities.' },
      { question: 'What specializations?', answer: 'AI/ML, gaming, media tech, entertainment platforms, and embedded systems.' },
      { question: 'How fast can you start?', answer: 'Team setup in 2-3 weeks.' },
      { question: 'IP protection?', answer: 'Full IP assignment, NDAs, Chinese and international compliance.' },
      { question: 'Can you scale?', answer: 'Start with 2 developers, scale to 25+.' },
      { question: 'Data security?', answer: 'ISO 27001 aligned, Chinese cybersecurity law compliant.' },
    ],
    localRate: '¥300-500', ourRate: '$15-26', savings: '60%+',
  },

  xiamen: {
    slug: 'software-development-agency-xiamen',
    cityName: 'Xiamen',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY',
    title: {
      en: 'Software Development Agency Xiamen | Call IT Dev',
      zh: '厦门软件开发公司 | Call IT Dev',
      fr: 'Agence Développement Logiciel Xiamen | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Xiamen, China. SaaS & e-commerce hub, SEZ advantages. From $16/hr.',
      zh: '厦门软件开发公司。SaaS和电商中心，经济特区优势。16美元/小时起。',
      fr: 'Agence de développement logiciel à Xiamen, Chine. Hub SaaS & e-commerce, dès 16 $/h.',
    },
    keywords: ['software development Xiamen', '厦门软件开发', 'SaaS development Xiamen'],
    heroTitle: { en: 'Software Development Agency in Xiamen', zh: '厦门软件开发公司', fr: 'Agence de Développement Logiciel à Xiamen' },
    heroSubtitle: {
      en: "China's SaaS capital. Beautiful coastal SEZ, strong e-commerce tech, and direct access to Taiwan Strait business ecosystem.",
      zh: '中国SaaS之都。美丽海滨经济特区，强大电商技术，直通台湾海峡商业生态。',
      fr: "Capitale SaaS de Chine. ZES côtière, tech e-commerce, accès direct à l'écosystème du détroit de Taiwan.",
    },
    whyTitle: { en: 'Why Xiamen for Software Development', zh: '为什么选择厦门进行软件开发', fr: 'Pourquoi Xiamen' },
    whyReasons: [
      { icon: '☁️', title: 'SaaS Capital', description: 'Xiamen hosts more SaaS companies per capita than any Chinese city.' },
      { icon: '🛒', title: 'E-commerce Hub', description: 'Major cross-border e-commerce center. Deep Shopify, Amazon, and marketplace expertise.' },
      { icon: '💰', title: '35% Below Shenzhen', description: 'Lower costs than Guangdong cities with excellent quality.' },
      { icon: '🏖️', title: 'Quality of Life', description: 'Beautiful coastal city with low turnover and high talent retention.' },
      { icon: '🎓', title: 'Xiamen University', description: 'Top-20 Chinese university with strong CS and business programs.' },
      { icon: '🔗', title: 'Taiwan Strait Access', description: 'Direct connections to Taiwan business ecosystem.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'What makes Xiamen unique?', answer: 'China\'s SaaS capital, major e-commerce hub, SEZ advantages, and beautiful coastal living for talent retention.' },
      { question: 'E-commerce expertise?', answer: 'Cross-border commerce, Shopify apps, Amazon integrations, marketplace platforms, and payment gateways.' },
      { question: 'Cost comparison?', answer: '35% below Shenzhen with comparable quality. SEZ tax advantages.' },
      { question: 'How fast?', answer: 'Team setup in 2 weeks.' },
      { question: 'IP protection?', answer: 'Full IP assignment, NDAs, and compliance.' },
      { question: 'Can you scale?', answer: 'Start with 2, scale to 20+.' },
    ],
    localRate: '¥350-550', ourRate: '$16-28', savings: '55%+',
  },

  kolkata: {
    slug: 'software-development-agency-kolkata',
    cityName: 'Kolkata',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Kolkata | Call IT Dev',
      fr: 'Agence Développement Logiciel Kolkata | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Kolkata, India. Emerging IT hub, IIT Kharagpur talent. From $12/hr. 75% savings.',
      fr: 'Agence de développement logiciel à Kolkata, Inde. Hub IT émergent, talents IIT Kharagpur, dès 12 $/h.',
    },
    keywords: ['software development Kolkata', 'IT outsourcing Kolkata', 'web development Kolkata India'],
    heroTitle: { en: 'Software Development Agency in Kolkata', fr: 'Agence de Développement Logiciel à Kolkata' },
    heroSubtitle: {
      en: "India's emerging IT destination. IIT Kharagpur & Jadavpur University talent, lowest costs in India, 75% savings vs Western rates.",
      fr: "Destination IT émergente de l'Inde. Talents IIT Kharagpur, coûts les plus bas, 75% d'économies.",
    },
    whyTitle: { en: 'Why Kolkata for Software Development', fr: 'Pourquoi Kolkata' },
    whyReasons: [
      { icon: '💰', title: 'Lowest Cost in India', description: 'Kolkata offers 20-30% lower costs than Bangalore/Pune/Chennai.' },
      { icon: '🎓', title: 'IIT Kharagpur', description: 'India\'s oldest IIT (1 hour away) produces world-class engineers.' },
      { icon: '🗣️', title: 'English Fluent', description: 'Strong English proficiency. Kolkata has a deep intellectual tradition.' },
      { icon: '🏢', title: 'Salt Lake IT Hub', description: 'Sector V Salt Lake hosts TCS, Wipro, Cognizant, and 500+ tech firms.' },
      { icon: '📈', title: 'Growing Ecosystem', description: 'Rapidly developing startup scene with government incentives.' },
      { icon: '⏰', title: 'IST Timezone', description: 'GMT+5:30 with 3-4h overlap with European business hours.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Why Kolkata?', answer: 'Lowest costs in India (20-30% below Bangalore), IIT Kharagpur talent, and growing tech ecosystem.' },
      { question: 'Quality comparison?', answer: 'Same university pipeline as other Indian cities but with significantly lower costs and attrition.' },
      { question: 'Infrastructure?', answer: 'Salt Lake Sector V is a modern IT hub with fiber connectivity and reliable power.' },
      { question: 'Can you scale?', answer: 'Start with 2 developers, scale to 30+ rapidly.' },
      { question: 'Data security?', answer: 'ISO 27001, GDPR aligned, DPDP Act compliant.' },
      { question: 'How fast?', answer: 'Team operational in 2 weeks.' },
    ],
    localRate: '$50-80', ourRate: '$12-20', savings: '75%+',
  },

  fez: {
    slug: 'software-development-agency-fez',
    cityName: 'Fez',
    countryName: 'Morocco',
    flag: '🇲🇦',
    timezone: 'CET (GMT+1)',
    currency: 'MAD/EUR',
    title: {
      en: 'Software Development Agency Fez | Call IT Dev',
      fr: 'Agence de Développement Informatique Fès | Call IT Dev',
      ar: 'وكالة تطوير برمجيات فاس | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Fez, Morocco. University city, emerging tech hub. French/English/Arabic. From $20/hr.',
      fr: 'Agence de développement informatique à Fès, Maroc. Ville universitaire, hub tech émergent, dès 20 $/h.',
      ar: 'وكالة تطوير برمجيات في فاس. مدينة جامعية، مركز تقني ناشئ. من 20$/ساعة.',
    },
    keywords: ['software development Fez', 'agence développement Fès', 'IT nearshore Fez Morocco', 'تطوير برمجيات فاس'],
    heroTitle: { en: 'Software Development Agency in Fez', fr: 'Agence de Développement Informatique à Fès', ar: 'وكالة تطوير برمجيات في فاس' },
    heroSubtitle: {
      en: "Morocco's intellectual capital. Historic university city with emerging tech hub, trilingual engineers, CET timezone. Part of the Call IT Dev Morocco network.",
      fr: "Capitale intellectuelle du Maroc. Ville universitaire historique, hub tech émergent, ingénieurs trilingues. Réseau Call IT Dev Maroc.",
      ar: 'عاصمة المغرب الفكرية. مدينة جامعية تاريخية، مركز تقني ناشئ، مهندسون ثلاثيو اللغة.',
    },
    whyTitle: { en: 'Why Fez for Software Development', fr: 'Pourquoi Fès', ar: 'لماذا فاس' },
    whyReasons: [
      { icon: '🎓', title: 'University City', description: 'Sidi Mohamed Ben Abdellah University and multiple engineering schools produce strong STEM graduates.' },
      { icon: '🌐', title: 'Trilingual Teams', description: 'French, English, Arabic — all native level. Seamless European and MENA communication.' },
      { icon: '💰', title: '15% Below Casablanca', description: 'Lower costs than Casablanca with equivalent quality from the same university pipeline.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone as Paris, Madrid, Berlin. Real-time European collaboration.' },
      { icon: '🏗️', title: 'Fez Shore', description: 'Emerging offshoring park with modern infrastructure and growing tech community.' },
      { icon: '🔗', title: 'CiD Network', description: 'Seamlessly connected to Call IT Dev HQ in Casablanca and hubs in Madrid & Dubai.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'How does Fez connect to Call IT Dev?', answer: 'Fez is part of our Morocco delivery network. Engineers in Fez are managed by our Casablanca HQ with identical quality standards.' },
      { question: 'Cost comparison?', answer: '15% lower than Casablanca. Same university-trained talent, same CET timezone.' },
      { question: 'Languages?', answer: 'French, English, Arabic — all native level.' },
      { question: 'Infrastructure?', answer: 'Fez Shore park + our own office with fiber and redundant power.' },
      { question: 'Can I combine Fez and Casablanca teams?', answer: 'Yes. We routinely scale across both cities for larger projects.' },
      { question: 'How fast?', answer: 'Team operational in 2 weeks via Casablanca coordination.' },
    ],
    localRate: '€70-100', ourRate: '€20-35', savings: '60%+',
  },

  hamburg: {
    slug: 'software-development-agency-hamburg',
    cityName: 'Hamburg',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Hamburg | Call IT Dev',
      de: 'Softwareentwicklung Agentur Hamburg | Call IT Dev',
      fr: 'Agence Développement Logiciel Hambourg | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Hamburg. German-speaking engineers, maritime & logistics tech. From €25/hr.',
      de: 'Softwareentwicklungsagentur für Hamburg. Deutschsprachige Ingenieure, Maritime & Logistik-Tech, ab 25 €/h.',
      fr: 'Agence de développement logiciel pour Hambourg. Ingénieurs germanophones, tech maritime, dès 25 €/h.',
    },
    keywords: ['Softwareentwicklung Hamburg', 'software development Hamburg', 'IT outsourcing Hamburg', 'nearshore Hamburg'],
    heroTitle: { en: 'Software Development Agency for Hamburg', de: 'Softwareentwicklung Agentur für Hamburg', fr: 'Agence de Développement Logiciel pour Hambourg' },
    heroSubtitle: {
      en: "German-speaking engineers for Hamburg's maritime & logistics ecosystem. CET timezone, 55% savings vs local rates.",
      de: 'Deutschsprachige Ingenieure für Hamburgs Maritime & Logistik. CET-Zeitzone, 55% Ersparnis.',
      fr: "Ingénieurs germanophones pour l'écosystème maritime de Hambourg. CET, 55% d'économies.",
    },
    whyTitle: { en: 'Why Hamburg Companies Choose Call IT Dev', de: 'Warum Hamburger Unternehmen Call IT Dev Wählen', fr: 'Pourquoi les Entreprises Hambourgeoises Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'German-Speaking', description: 'Engineers fluent in German for Hamburg client communication.' },
      { icon: '🚢', title: 'Maritime Tech', description: 'Hamburg is Europe\'s 3rd largest port. Deep logistics & shipping tech expertise.' },
      { icon: '💰', title: '55% Savings', description: '€25-42/hr vs €85-120/hr in Hamburg.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone. Real-time collaboration.' },
      { icon: '🏢', title: 'Enterprise Ready', description: 'Experience with Airbus, Otto Group, and Hamburg-based enterprises.' },
      { icon: '🔒', title: 'DSGVO Compliant', description: 'Full German data protection compliance.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Maritime tech experience?', answer: 'Port management systems, vessel tracking, logistics platforms, supply chain visibility, and maritime IoT.' },
      { question: 'German language support?', answer: 'Yes. B2/C1+ German-speaking engineers for Hamburg projects.' },
      { question: 'Cost?', answer: '€25-42/hr vs €85-120/hr in Hamburg. 55% savings.' },
      { question: 'DSGVO?', answer: 'Fully DSGVO/GDPR compliant with ISO 27001 certification.' },
      { question: 'Can you integrate?', answer: 'Staff augmentation — embedded in your workflow.' },
      { question: 'How fast?', answer: 'Team setup in 2-3 weeks.' },
    ],
    localRate: '€85-120', ourRate: '€25-42', savings: '55%+',
  },

  barcelona: {
    slug: 'software-development-agency-barcelona',
    cityName: 'Barcelona',
    countryName: 'Spain',
    flag: '🇪🇸',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Barcelona | Call IT Dev',
      es: 'Agencia Desarrollo Software Barcelona | Call IT Dev',
      fr: 'Agence Développement Logiciel Barcelone | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Barcelona. Spanish/English engineers, startup ecosystem. From €25/hr. Via our Madrid hub.',
      es: 'Agencia de desarrollo de software para Barcelona. Ingenieros ES/EN, ecosistema startup. Desde 25 €/h. Vía nuestro hub Madrid.',
      fr: 'Agence de développement logiciel pour Barcelone. Ingénieurs ES/EN, écosystème startup, dès 25 €/h.',
    },
    keywords: ['software development Barcelona', 'agencia desarrollo software Barcelona', 'nearshore development Barcelona', 'desarrollo web Barcelona'],
    heroTitle: { en: 'Software Development Agency for Barcelona', es: 'Agencia de Desarrollo de Software para Barcelona', fr: 'Agence de Développement Logiciel pour Barcelone' },
    heroSubtitle: {
      en: "Serving Barcelona's startup & tech ecosystem from our Madrid hub. Spanish/English engineers, CET timezone, 55% savings vs local agencies.",
      es: 'Servimos al ecosistema startup de Barcelona desde nuestro hub Madrid. Ingenieros ES/EN, zona CET, 55% ahorro.',
      fr: "Au service de l'écosystème startup de Barcelone depuis notre hub Madrid. 55% d'économies.",
    },
    whyTitle: { en: 'Why Barcelona Companies Choose Call IT Dev', es: 'Por Qué las Empresas de Barcelona Eligen Call IT Dev', fr: 'Pourquoi les Entreprises Barcelonaises Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Spanish & English', description: 'Bilingual engineers for Barcelona\'s international startup scene.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone as Barcelona. Real-time collaboration.' },
      { icon: '💰', title: '55% Savings', description: '€25-42/hr vs €70-110/hr in Barcelona.' },
      { icon: '🚀', title: 'Startup Ready', description: 'Agile teams for MVPs, scale-ups, and product development.' },
      { icon: '🔗', title: 'Madrid Hub Connection', description: 'Coordinated via our Madrid European Hub for EU compliance.' },
      { icon: '🇪🇺', title: 'EU Contracts', description: 'EU entity in Madrid for contracts and GDPR compliance.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Do you have an office in Barcelona?', answer: 'Our EU hub is in Madrid. We serve Barcelona remotely with CET timezone alignment and frequent visits (1h AVE train).' },
      { question: 'Startup experience?', answer: 'Extensive. MVP development, product-market fit, Series A/B scaling for Barcelona startups.' },
      { question: 'Cost?', answer: '€25-42/hr vs €70-110/hr locally. 55% savings.' },
      { question: 'EU compliance?', answer: 'Full GDPR via our Madrid EU entity.' },
      { question: 'Languages?', answer: 'Spanish, English, French, Catalan understanding.' },
      { question: 'How fast?', answer: 'Team setup in 2 weeks.' },
    ],
    localRate: '€70-110', ourRate: '€25-42', savings: '55%+',
  },

  kuwaitCity: {
    slug: 'software-development-agency-kuwait-city',
    cityName: 'Kuwait City',
    countryName: 'Kuwait',
    flag: '🇰🇼',
    timezone: 'AST (GMT+3)',
    currency: 'KWD/USD',
    title: {
      en: 'Software Development Agency Kuwait City | Call IT Dev',
      ar: 'شركة تطوير برمجيات الكويت | Call IT Dev',
      fr: 'Agence Développement Logiciel Koweït | Call IT Dev',
    },
    description: {
      en: 'Software development agency for Kuwait City. Arabic/English bilingual, fintech & govtech. From $22/hr. Coordinated via Dubai hub.',
      ar: 'شركة تطوير برمجيات للكويت. ثنائي اللغة عربي/إنجليزي، فنتك وتقنية حكومية. من 22$/ساعة.',
      fr: 'Agence de développement logiciel pour Koweït. Bilingue arabe/anglais, fintech & govtech, dès 22 $/h.',
    },
    keywords: ['software development Kuwait', 'IT outsourcing Kuwait', 'تطوير برمجيات الكويت', 'fintech Kuwait'],
    heroTitle: { en: 'Software Development Agency for Kuwait City', ar: 'شركة تطوير برمجيات للكويت', fr: 'Agence de Développement Logiciel pour Koweït' },
    heroSubtitle: {
      en: "Serving Kuwait's digital transformation from our Dubai MENA hub. Arabic/English teams, Islamic fintech, Vision 2035 alignment.",
      ar: 'خدمة التحول الرقمي في الكويت من مركزنا في دبي. فرق عربية/إنجليزية، فنتك إسلامي.',
      fr: "Au service de la transformation digitale du Koweït depuis notre hub MENA de Dubaï.",
    },
    whyTitle: { en: 'Why Kuwait Companies Choose Call IT Dev', ar: 'لماذا تختار الشركات الكويتية Call IT Dev', fr: 'Pourquoi les Entreprises Koweïtiennes Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🌍', title: 'Dubai Hub Connection', description: 'Coordinated from our Dubai MENA hub. Kuwait City is 1h flight from Dubai.' },
      { icon: '🗣️', title: 'Arabic Native', description: 'Native Arabic-speaking engineers with Kuwaiti dialect understanding.' },
      { icon: '🕌', title: 'Islamic Fintech', description: 'Sharia-compliant banking, payment systems, and halal commerce expertise.' },
      { icon: '💰', title: '55% Savings', description: '$22-38/hr vs $60-120/hr locally. Premium quality at competitive prices.' },
      { icon: '🏛️', title: 'Vision 2035', description: 'Supporting Kuwait New Kuwait Vision 2035 digital initiatives.' },
      { icon: '🔒', title: 'Data Compliance', description: 'Kuwaiti data protection and cybersecurity framework compliance.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Do you have an office in Kuwait?', answer: 'We serve Kuwait from our Dubai MENA hub (1h flight). Arabic-native teams with GCC market expertise.' },
      { question: 'Islamic fintech?', answer: 'Yes. Sharia-compliant banking, Islamic payment gateways, halal commerce platforms.' },
      { question: 'Cost?', answer: '$22-38/hr vs $60-120/hr locally. 55% savings.' },
      { question: 'Data residency?', answer: 'We support Kuwait data residency and comply with CITRA regulations.' },
      { question: 'Contract?', answer: 'Via our Dubai entity (UBTIS) with GCC-standard agreements.' },
      { question: 'How fast?', answer: 'Team setup in 2-3 weeks.' },
    ],
    localRate: '$60-120', ourRate: '$22-38', savings: '55%+',
  },

  manchester: {
    slug: 'software-development-agency-manchester',
    cityName: 'Manchester',
    countryName: 'United Kingdom',
    flag: '🇬🇧',
    timezone: 'GMT/BST',
    currency: 'GBP',
    title: {
      en: 'Software Development Agency Manchester | Call IT Dev',
      fr: 'Agence Développement Logiciel Manchester | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Manchester. English-native engineers, fintech & e-commerce. From £20/hr. 65% savings.',
      fr: 'Agence de développement logiciel pour Manchester. Ingénieurs anglophones, fintech & e-commerce, dès 20 £/h.',
    },
    keywords: ['software development Manchester', 'IT outsourcing Manchester', 'nearshore development Manchester UK', 'web development Manchester'],
    heroTitle: { en: 'Software Development Agency for Manchester', fr: 'Agence de Développement Logiciel pour Manchester' },
    heroSubtitle: {
      en: "English-native engineers for Manchester's digital economy. 1-hour time difference, fintech & e-commerce expertise, 65% savings.",
      fr: "Ingénieurs anglophones pour l'économie digitale de Manchester. 1h de décalage, 65% d'économies.",
    },
    whyTitle: { en: 'Why Manchester Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Manchester Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English Native', description: 'Fluent English engineers. No communication barriers.' },
      { icon: '⏰', title: '1-Hour Difference', description: 'GMT+1 — just 1 hour ahead of Manchester. Full overlap.' },
      { icon: '💰', title: '65% Savings', description: '£20-35/hr vs £65-110/hr in Manchester.' },
      { icon: '🏦', title: 'Fintech', description: 'Manchester is the UK\'s #2 fintech hub. We build financial tech at scale.' },
      { icon: '🛒', title: 'E-commerce', description: 'Deep expertise in e-commerce platforms for the Northern Powerhouse.' },
      { icon: '🛡️', title: 'UK GDPR', description: 'ISO 27001, UK GDPR compliant, ICO-ready.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Do you serve Manchester?', answer: 'Yes. Dedicated teams for Manchester and Northern England with UK GDPR compliance.' },
      { question: 'Fintech?', answer: 'Payment platforms, lending, insurtech, and open banking — aligned with Manchester\'s fintech cluster.' },
      { question: 'Cost?', answer: '£20-35/hr vs £65-110/hr locally. 65% savings.' },
      { question: 'UK compliance?', answer: 'UK GDPR, ICO-ready, ISO 27001, DPA agreements.' },
      { question: 'Integration?', answer: 'Staff augmentation — embedded in your existing Manchester team workflow.' },
      { question: 'How fast?', answer: 'Team operational in 2 weeks.' },
    ],
    localRate: '£65-110', ourRate: '£20-35', savings: '65%+',
  },
};
